import { useEffect, useRef, useState } from "react";
import PropertyFacts from "../components/PropertyFacts";
import Spinner from "../components/Spinner";
import RegenerateBar from "../components/RegenerateBar";
import VendorBrowser from "../marketplace/VendorBrowser";
import DocVerification from "../components/DocVerification";
import { callAI, normalizeAddressKey, track } from "../lib/api";
import { propFactsLine } from "../lib/property";

// Stage 2 — Pre-listing prep.
//   Two sub-sections:
//     A) AI ROI-ranked repair list (auto-runs on entry)
//     B) Inspector marketplace — seller picks a pre-listing inspector. The
//        inspector's report (when received) feeds the Disclosures stage so
//        the seller doesn't have to self-report defects the inspector found.
//
//   The stage's `state.data` now holds both:
//     { roi_items, picked_vendor_id, quote, accepted_at, inspection_findings? }

const SYSTEM = "You are a home-prep specialist. Given a property's basic facts, produce a tight ROI-ranked list of small pre-listing fixes that materially move sale price. Focus on the under-$2k items most sellers skip. Be specific to the property's age.";

const PROMPT = (address, factsLine, note) => `Property: ${address}
${factsLine}

Output 5-7 ROI-ranked recommendations. JSON array only, no commentary, no code fence:
[
  { "id": "kebab-id", "task": "what to fix/refresh", "cost_est": "$X-$Y", "value_lift": "~$X", "priority": "high|med|low", "why": "1 sentence" }
]

Order from highest priority to lowest. Realistic costs for this property's actual local market — infer typical contractor/material pricing from the property's city and state, not any other region.
${note ? `Seller's guidance for this regeneration — factor this in: ${note}` : ""}`;

const safeParseJSON = (text) => {
  const cleaned = (text || "").replace(/```(?:json)?/gi, "").trim();
  const start = cleaned.indexOf("[");
  const end = cleaned.lastIndexOf("]");
  if (start < 0 || end < 0) return null;
  try { return JSON.parse(cleaned.slice(start, end + 1)); } catch { return null; }
};

const priorityColor = { high: "#c0354a", med: "#c47c1a", low: "#7aad98" };

export default function PrepStage({ state, journey, update }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(state.data?.roi_items || []);
  const [err, setErr] = useState(null);
  const fired = useRef(false);

  const runROI = async (note = "") => {
    setLoading(true); setErr(null);
    update({ status: "in_progress" });
    track("seller_prep_roi_run", { has_note: Boolean(note) });
    try {
      const { text, cached } = await callAI({
        system: SYSTEM,
        prompt: PROMPT(journey.address, propFactsLine(journey.property), note),
        maxTokens: 1200,
        cacheKey: note ? undefined : `prep:${normalizeAddressKey(journey.address)}`,
        cacheType: note ? undefined : "prep",
      });
      if (cached) track("seller_prep_cache_hit");
      const parsed = safeParseJSON(text);
      if (!parsed || !Array.isArray(parsed) || !parsed.length) throw new Error("bad-json");
      setItems(parsed);
      update({
        data: { ...(state.data || {}), roi_items: parsed, generated_at: new Date().toISOString() },
      });
      track("seller_prep_roi_done", { count: parsed.length });
    } catch {
      setErr("Couldn't generate prep recommendations. Try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!state.data?.roi_items) runROI();
  }, []);

  return (
    <>
      <PropertyFacts property={journey.property} />

      {/* ───── A · ROI repair recommendations ───── */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "#152d21", margin: "0 0 6px", fontWeight: 700 }}>
          ROI-ranked prep work
        </h3>
        <div className="s-ai-pill" style={{ marginBottom: 12 }}>★ AI · Ranked for this property's age and market</div>

        {loading && <Spinner label="Analyzing your property…" />}

        {!loading && err && (
          <div>
            <div className="s-intake-err" style={{ marginBottom: 12 }}>{err}</div>
            <button type="button" className="s-btn amber" onClick={() => runROI()}>Try again</button>
          </div>
        )}

        {!loading && items.length > 0 && items.map((it) => (
          <div key={it.id || it.task} className="s-vendor passive" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{
                fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: "0.08em",
                textTransform: "uppercase", color: priorityColor[it.priority] || "#9aa9a0", fontWeight: 700,
              }}>{it.priority || "—"}</span>
              <div className="s-vendor-name">{it.task}</div>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div className="s-vendor-meta">cost: {it.cost_est}</div>
              <div className="s-vendor-meta green">lift: {it.value_lift}</div>
            </div>
            {it.why && (
              <div className="s-vendor-meta" style={{ marginTop: 6, fontFamily: "'Outfit',sans-serif", letterSpacing: 0, color: "#5b7365", lineHeight: 1.5 }}>
                {it.why}
              </div>
            )}
          </div>
        ))}

        {!loading && items.length > 0 && (
          <RegenerateBar
            onRegenerate={(note) => runROI(note)}
            loading={loading}
            placeholder="e.g. focus on curb appeal, budget under $500 each…"
          />
        )}
      </div>

      {/* ───── B · Inspector marketplace ───── */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "#152d21", margin: "0 0 6px", fontWeight: 700 }}>
          Pre-listing inspection
        </h3>
        <p style={{ fontSize: 13, color: "#5b7365", lineHeight: 1.6, margin: "0 0 12px" }}>
          Book a pre-listing inspector. Their report auto-feeds your Disclosures
          step — anything they find becomes a known defect you must disclose
          anyway, so we'd rather you know first.
        </p>
        <VendorBrowser
          type="inspector"
          journey={journey}
          state={state}
          update={update}
          stageId="prep"
        />
      </div>

      {/* ───── C · Inspection report upload (post-inspection) ───── */}
      {state.data?.accepted_at && (
        <div>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "#152d21", margin: "0 0 6px", fontWeight: 700 }}>
            Upload inspection report when received
          </h3>
          <p style={{ fontSize: 13, color: "#5b7365", lineHeight: 1.6, margin: "0 0 12px" }}>
            Once your inspector emails the PDF, drop it here. AI extracts the
            structured findings and pre-fills your Disclosures so you don't
            re-type what the inspector already documented.
          </p>
          <DocVerification
            type="inspection"
            journey={journey}
            label="Inspection report (PDF)"
            helpText="We parse roof, electrical, plumbing, foundation, HVAC, water damage, pests, and material defects."
            onComplete={(result) => {
              if (result.findings) {
                update({
                  data: {
                    ...(state.data || {}),
                    inspection_findings: result.findings,
                    inspection_verification_id: result.verification_id,
                    inspection_uploaded_at: new Date().toISOString(),
                  },
                });
                track("seller_inspection_parsed", { defects: result.findings?.material_defects?.length || 0 });
              }
            }}
          />
        </div>
      )}
    </>
  );
}
